import {
  isVectorSource,
  type FieldDefinition,
  type JsonPrimitive,
  type SourceDescriptor,
  type TableScalarValue,
  type TableSourceDescriptor,
  type VectorFeature,
  type VectorFeatureCollection,
  type VectorSourceDescriptor,
} from '../model';
import {
  effectiveFieldsForLayer,
  effectiveVectorSourceForLayer,
  isJoinedEffectiveField,
} from '../../joins/effectiveLayer';
import type { LayerDefinition } from '../../project/model';
import type { SelectedFeatureRef } from '../../selection/model';
import {
  DataExportError,
  type DataExportField,
  type TabularDataExport,
  type VectorExportScope,
} from './model';

const SCALAR_TYPES = new Set<FieldDefinition['type']>([
  'boolean',
  'date',
  'integer',
  'number',
  'string',
]);

export interface VectorDataExportPlan {
  readonly canonicalSource: VectorSourceDescriptor;
  readonly effectiveSource: VectorSourceDescriptor;
  readonly featureIndices: readonly number[];
  readonly fields: readonly DataExportField[];
  readonly layer: LayerDefinition;
  readonly scope: VectorExportScope;
}

function requireScalarType(field: FieldDefinition): DataExportField['type'] {
  if (!SCALAR_TYPES.has(field.type)) {
    throw new DataExportError(`Field “${field.name}” does not contain exportable scalar data.`);
  }
  return field.type as DataExportField['type'];
}

function uniqueOutputName(base: string, used: Set<string>): string {
  if (!used.has(base)) {
    used.add(base);
    return base;
  }
  let suffix = 2;
  while (used.has(`${base} (${suffix})`)) suffix += 1;
  const name = `${base} (${suffix})`;
  used.add(name);
  return name;
}

export function planVectorExportFields(
  sources: readonly SourceDescriptor[],
  layer: LayerDefinition,
  includeJoinedFields: boolean,
): readonly DataExportField[] {
  const used = new Set<string>();
  return effectiveFieldsForLayer(sources, layer).flatMap((field) => {
    const joined = isJoinedEffectiveField(field);
    if (joined && !includeJoinedFields) return [];
    const join = joined
      ? layer.joins.find((candidate) => candidate.id === field.origin.joinId)
      : null;
    const base = joined
      ? `${join?.name ?? 'Joined table'} · ${field.origin.sourceField}`
      : field.name;
    return [
      {
        sourceField: field.name,
        outputField: uniqueOutputName(base, used),
        type: requireScalarType(field),
        origin: joined ? ('joined' as const) : ('native' as const),
      },
    ];
  });
}

function selectedFeatureIndices(
  selection: readonly SelectedFeatureRef[],
  layer: LayerDefinition,
  source: VectorSourceDescriptor,
): readonly number[] {
  const selected = new Set(
    selection.flatMap((reference) =>
      reference.layerId === layer.id && reference.sourceId === source.id
        ? [reference.sourceFeatureIndex]
        : [],
    ),
  );
  return source.data.features.flatMap((_, index) => (selected.has(index) ? [index] : []));
}

export function planVectorDataExport(
  sources: readonly SourceDescriptor[],
  layer: LayerDefinition,
  selection: readonly SelectedFeatureRef[],
  options: { readonly includeJoinedFields: boolean; readonly scope: VectorExportScope },
): VectorDataExportPlan {
  const canonical = sources.find((source) => source.id === layer.sourceId);
  if (!canonical || !isVectorSource(canonical)) {
    throw new DataExportError('The vector source is no longer available.');
  }
  const effective = effectiveVectorSourceForLayer(sources, layer);
  if (!effective) throw new DataExportError('The effective vector data could not be prepared.');
  return {
    canonicalSource: canonical,
    effectiveSource: effective,
    featureIndices:
      options.scope === 'all'
        ? canonical.data.features.map((_, index) => index)
        : selectedFeatureIndices(selection, layer, canonical),
    fields: planVectorExportFields(sources, layer, options.includeJoinedFields),
    layer,
    scope: options.scope,
  };
}

function scalarValue(value: unknown, fieldName: string): JsonPrimitive {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' || typeof value === 'boolean') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  throw new DataExportError(
    `Field “${fieldName}” contains a value that cannot be exported safely.`,
  );
}

function exportProperties(feature: VectorFeature, fields: readonly DataExportField[]) {
  return Object.fromEntries(
    fields.map((field) => [
      field.outputField,
      scalarValue(feature.properties?.[field.sourceField], field.outputField),
    ]),
  );
}

export function buildVectorGeoJson(plan: VectorDataExportPlan): VectorFeatureCollection {
  return {
    type: 'FeatureCollection',
    features: plan.featureIndices.map((index) => {
      const feature = plan.effectiveSource.data.features[index];
      if (!feature) throw new DataExportError('A selected feature is no longer available.');
      return {
        type: 'Feature' as const,
        geometry: feature.geometry,
        properties: exportProperties(feature, plan.fields),
        ...(feature.id === undefined ? {} : { id: feature.id }),
      };
    }),
  };
}

export function buildVectorTable(plan: VectorDataExportPlan): TabularDataExport {
  return {
    fields: plan.fields,
    rows: plan.featureIndices.map((index) => {
      const feature = plan.effectiveSource.data.features[index];
      if (!feature) throw new DataExportError('A selected feature is no longer available.');
      return plan.fields.map((field) =>
        scalarValue(feature.properties?.[field.sourceField], field.outputField),
      ) as readonly TableScalarValue[];
    }),
  };
}

export function planTableDataExport(source: TableSourceDescriptor): TabularDataExport {
  return {
    fields: source.fields.map((field) => ({
      sourceField: field.name,
      outputField: field.name,
      type: requireScalarType(field),
      origin: 'native',
    })),
    rows: source.rows,
  };
}
